import { QueryParameterBag } from '@smithy/types'

import {
  DroidMapMetadata,
  Paged,
  ServiceClient,
  ServiceClientContext,
  StandardOrgIds,
  queryParameterBagFromPaginationControls,
} from '@droidmap/shared-service-contract'
import type { LoggerLike as Logger } from '@droidmap/shared-telemetry'
import { PermissionDeniedError, ensureError } from '@droidmap/shared-utils'

import {
  BootstrapOrganisationGrantsInput,
  BootstrapOrganisationMemberGrantsInput,
  BootstrapProjectGrantsInput,
  CreateGrantInput,
  DeleteGrantInput,
  GetGrantInput,
  Grant,
  ListGrantsByPrincipalInput,
  ListGrantsByResourceInput,
  ListOrgAdminAccessInput,
  OrgAdminAccess,
} from '../models/grant.js'
import { isPermissionAllowedByTokenScopes } from '../models/permission.js'
import {
  CheckPermissionInput,
  CheckPermissionOutput,
  GetResourcesWithPermissionInput,
  GetResourcesWithPermissionOutput,
} from '../models/permissions-check.js'
import {
  DroidMapRolePermissions,
  RolePermissions,
} from '../models/role-permissions.js'
import { Role } from '../models/role.js'
import {
  CreateSupportAccessGrantRequest,
  ListSupportAccessGrantsInput,
  SupportAccessGrant,
} from '../models/support-access.js'
import {
  GetUserGrantInput,
  ListUserGrantsByGroupAndResourceAndRoleInput,
  ListUserGrantsByUserAndResourceInput,
  ListUserGrantsByUserInput,
  UserGrant,
} from '../models/user-grant.js'

export class PermissionsServiceClient extends ServiceClient {
  public static readonly SERVICE_NAME = 'PermissionsService'

  constructor(input: ServiceClientContext) {
    super({ ...input, serviceName: PermissionsServiceClient.SERVICE_NAME })
  }

  getRolePermissions = (role: Role): RolePermissions => {
    const rolePermissions = DroidMapRolePermissions.get(role)
    if (!rolePermissions) {
      throw new Error(`Role ${role} does not exist`)
    }

    return rolePermissions
  }

  /* ---------------------------------- Grant --------------------------------- */

  createGrant = async (
    input: CreateGrantInput,
    metadata?: DroidMapMetadata,
  ): Promise<Grant> => {
    const grant = await this.callApiRouteWithToken<CreateGrantInput, Grant>({
      method: 'POST',
      path: '/grants',
      body: input,
      metadata,
    })

    return grant
  }

  /**
   * Synchronously bootstrap the grants for a newly created organisation
   * (#2086). Creates the creator's `ORG_ADMIN` grant and the `Administrators`
   * group's `ORG_ADMIN` grant before returning, so the caller can rely on both
   * grants existing once org creation completes. Backed by the internal-only
   * `POST /grants/internal/bootstrap-organisation` route; org creation rolls
   * back if this call fails, so there is no async fallback.
   */
  bootstrapOrganisationGrants = async (
    input: BootstrapOrganisationGrantsInput,
    metadata?: DroidMapMetadata,
  ): Promise<void> => {
    await this.postInternalBootstrap(
      '/grants/internal/bootstrap-organisation',
      input,
      metadata,
    )
  }

  /**
   * Synchronously give a member who has just joined an organisation the
   * baseline `ORG_READ` grant (#3062). Backed by the internal-only
   * `POST /grants/internal/bootstrap-member` route; idempotent, so a retried
   * invitation accept can call it again safely.
   */
  bootstrapOrganisationMemberGrants = async (
    input: BootstrapOrganisationMemberGrantsInput,
    metadata?: DroidMapMetadata,
  ): Promise<void> => {
    await this.postInternalBootstrap(
      '/grants/internal/bootstrap-member',
      input,
      metadata,
    )
  }

  /**
   * Synchronously bootstrap the grants for a newly created project (#2086).
   * Creates the creator's `PROJECT_ADMIN` grant and copies the org's
   * `ORG_ADMIN` grants down to the project before returning, so the caller can
   * rely on those grants existing once project creation completes. Backed by
   * the internal-only `POST /grants/internal/bootstrap-project` route; project
   * creation rolls back if this call fails, so there is no async fallback.
   */
  bootstrapProjectGrants = async (
    input: BootstrapProjectGrantsInput,
    metadata?: DroidMapMetadata,
  ): Promise<void> => {
    await this.postInternalBootstrap(
      '/grants/internal/bootstrap-project',
      input,
      metadata,
    )
  }

  /**
   * Shared transport for the internal bootstrap routes: a fire-and-forget POST
   * (the routes return 204 No Content) gated to internal service callers.
   */
  private postInternalBootstrap = async (
    path: string,
    input:
      | BootstrapOrganisationGrantsInput
      | BootstrapOrganisationMemberGrantsInput
      | BootstrapProjectGrantsInput,
    metadata?: DroidMapMetadata,
  ): Promise<void> => {
    await this.callApiRouteWithToken<typeof input, unknown>({
      method: 'POST',
      path,
      body: input,
      metadata,
    })
  }

  getGrant = async (
    input: GetGrantInput,
    metadata?: DroidMapMetadata,
  ): Promise<Grant> => {
    const grant = await this.callApiRouteWithToken<GetGrantInput, Grant>({
      method: 'GET',
      path: `/grants/${input.resourceId}/${input.principalId}/${input.role}`,
      body: input,
      metadata,
    })

    return grant
  }

  deleteGrant = async (
    input: DeleteGrantInput,
    metadata?: DroidMapMetadata,
  ): Promise<Grant> => {
    const grant = await this.callApiRouteWithToken<DeleteGrantInput, Grant>({
      method: 'DELETE',
      path: '/grants',
      body: input,
      metadata,
    })

    return grant
  }

  listGrantsByResource = async (
    input: ListGrantsByResourceInput,
    metadata?: DroidMapMetadata,
  ): Promise<Paged<Grant>> => {
    const { resourceId, next, prev, pageSize } = input

    const queryParameters: QueryParameterBag =
      queryParameterBagFromPaginationControls({
        next,
        prev,
        pageSize,
      })

    const grants = await this.callApiRouteWithToken<
      ListGrantsByResourceInput,
      Paged<Grant>
    >({
      method: 'GET',
      path: `/grants/resource/${resourceId}`,
      queryParameters,
      metadata,
    })

    return grants
  }

  /**
   * List the users who can access a resource because they hold `ORG_ADMIN` on
   * its organisation, each with how they hold it: directly, via one or more
   * groups, or both (#3085). Read from the organisation's own grants, so it
   * does not depend on the copies made onto each project. Needs the same
   * `LIST_GRANTS` permission on the resource as {@link listGrantsByResource}.
   */
  listOrgAdminAccess = async (
    input: ListOrgAdminAccessInput,
    metadata?: DroidMapMetadata,
  ): Promise<OrgAdminAccess[]> => {
    return this.callApiRouteWithToken<undefined, OrgAdminAccess[]>({
      method: 'GET',
      path: `/grants/inherited/${input.resourceId}`,
      metadata,
    })
  }

  /**
   * List grants by resource for **internal/service callers** (#2053). Unlike
   * {@link listGrantsByResource}, the server skips the per-caller `LIST_GRANTS`
   * permission check (a service principal holds no role on the resource), so
   * the route is gated to internal callers. Use this when a backend service
   * needs to enumerate a resource's grants (e.g. an org's admins).
   */
  listGrantsByResourceInternal = async (
    input: ListGrantsByResourceInput,
    metadata?: DroidMapMetadata,
  ): Promise<Paged<Grant>> => {
    const { resourceId, next, prev, pageSize } = input

    const queryParameters: QueryParameterBag =
      queryParameterBagFromPaginationControls({
        next,
        prev,
        pageSize,
      })

    return this.callApiRouteWithToken<ListGrantsByResourceInput, Paged<Grant>>({
      method: 'GET',
      path: `/grants/internal/${resourceId}`,
      queryParameters,
      metadata,
    })
  }

  /**
   * DroidMap support's live grants on a resource, newest first. Internal
   * callers only; lapsed and revoked grants are not returned.
   */
  listActiveSupportAccessInternal = async (
    input: ListSupportAccessGrantsInput,
    metadata?: DroidMapMetadata,
  ): Promise<{ items: SupportAccessGrant[] }> => {
    return this.callApiRouteWithToken<
      undefined,
      { items: SupportAccessGrant[] }
    >({
      method: 'GET',
      path: `/support-access/internal/${input.resourceType}/${input.resourceId}`,
      metadata,
    })
  }

  /**
   * Revoke a support access grant on any resource. Internal callers only: the
   * admin service ends a session's grant with the session.
   */
  revokeSupportAccessInternal = async (
    input: ListSupportAccessGrantsInput & { grantId: string },
    metadata?: DroidMapMetadata,
  ): Promise<{ revoked: true }> => {
    return this.callApiRouteWithToken<undefined, { revoked: true }>({
      method: 'DELETE',
      path: `/support-access/internal/${input.resourceType}/${input.resourceId}/${input.grantId}`,
      metadata,
    })
  }

  /**
   * Give DroidMap support time-boxed access. The admin service only: the
   * route requires its signed assertion for exactly this grant.
   */
  createSupportAccessInternal = async (
    input: CreateSupportAccessGrantRequest,
    metadata?: DroidMapMetadata,
  ): Promise<SupportAccessGrant> => {
    return this.callApiRouteWithToken<
      CreateSupportAccessGrantRequest,
      SupportAccessGrant
    >({
      method: 'POST',
      path: '/support-access/internal',
      body: input,
      metadata,
    })
  }

  listGrantsByPrincipal = async (
    input: ListGrantsByPrincipalInput,
    metadata?: DroidMapMetadata,
  ): Promise<Paged<Grant>> => {
    const { principalId, next, prev, pageSize } = input

    const queryParameters: QueryParameterBag =
      queryParameterBagFromPaginationControls({
        next,
        prev,
        pageSize,
      })

    const grants = await this.callApiRouteWithToken<
      ListGrantsByPrincipalInput,
      Paged<Grant>
    >({
      method: 'GET',
      path: `/grants/principal/${principalId}`,
      queryParameters,
      metadata,
    })

    return grants
  }

  /* ---------------------------- Check Permission ---------------------------- */

  /**
   * Call this if you want to test if a user has a permission.
   * This does not throw an error if the user does not have the permission and
   * returns a boolean.
   *
   * @returns boolean (true if the user has the permission, false otherwise)
   */
  hasPermission = async (
    input: CheckPermissionInput & { metadata?: DroidMapMetadata },
  ): Promise<boolean> => {
    // The `system` sentinel is what a caller sends before it has chosen an
    // org, and no grant can exist on it. Answer here rather than calling: the
    // permissions service validates `resourceId` as a ULID and 400s the
    // sentinel, and every caller turned that into a 502 (RCA-0003, #3030).
    if (input.resourceId === StandardOrgIds.SYSTEM) return false

    // Extract only CheckPermissionInput fields for the body (exclude metadata to avoid circular refs)
    const body: CheckPermissionInput = {
      userId: input.userId,
      resourceId: input.resourceId,
      permission: input.permission,
    }

    const checkPermissionOutput = await this.callApiRouteWithToken<
      CheckPermissionInput,
      CheckPermissionOutput
    >({
      method: 'POST', // POST method is used to check permissions
      path: '/permissions/check',
      body,
      metadata: input.metadata,
    })

    return checkPermissionOutput.result
  }

  /**
   * Call this if you want to test if a user has a permission.
   * This throws a PermissionDeniedError if the user does not have the permission.
   * If you pass in a logger it will log the error.
   *
   * For token-based auth, validates that the token's scopes allow the permission.
   * Token scopes are sufficient for authorization - no role lookup is performed.
   * This is because the user's role was validated when the token was created,
   * and token revocation is the mechanism to remove access.
   */
  check = async (
    input: CheckPermissionInput & {
      metadata?: DroidMapMetadata
      log?: Logger
      extraLoggingKeys?: Record<string, unknown>
    },
  ): Promise<void> => {
    const { userId, resourceId, permission, metadata, log, extraLoggingKeys } =
      input

    const permissionContext = {
      userId,
      resourceId,
      permission,
      metadata,
    }

    // For token-based auth, token scopes ARE the permission.
    // Role was validated at token creation time, and revocation is the
    // mechanism to remove access. Skip the role lookup for performance.
    if (metadata?.tokenScopes && metadata.tokenScopes.length > 0) {
      if (!isPermissionAllowedByTokenScopes(permission, metadata.tokenScopes)) {
        if (log) {
          log.error(
            'Permission denied - token scope does not allow this permission',
            {
              ...permissionContext,
              tokenScopes: metadata.tokenScopes,
              ...extraLoggingKeys,
            },
          )
        }
        throw new PermissionDeniedError('Permission denied', {
          context: { ...permissionContext, ...extraLoggingKeys },
        })
      }
      // Token scope grants permission - no need for role lookup
      return
    }

    // For non-token auth (Cognito), check role-based permission
    let hasPermission = false
    try {
      hasPermission = await this.hasPermission({
        ...input,
      })
    } catch (error) {
      if (log) {
        const err = ensureError(error)
        log.error('Error checking permission', {
          name: err.name,
          message: err.message,
          stack: err.stack,
        })
      }
      throw error
    }

    if (!hasPermission) {
      if (log) {
        log.warn('Permission denied', {
          ...permissionContext,
          ...extraLoggingKeys,
        })
      }
      throw new PermissionDeniedError('Permission denied', {
        context: { ...permissionContext, ...extraLoggingKeys },
      })
    }
  }

  /**
   * Can be used to return the list of project ids that a user has a specific
   * permission for within an organisation.
   *
   */
  getResourcesWithPermission = async (
    input: GetResourcesWithPermissionInput,
    metadata?: DroidMapMetadata,
  ): Promise<GetResourcesWithPermissionOutput> => {
    // As `hasPermission`: the route validates `orgId` as a ULID, and callers
    // pass the header org, which may be the `system` sentinel (#3030).
    if (input.orgId === StandardOrgIds.SYSTEM) return []

    const getResourcesWithPermissionsOutput = await this.callApiRouteWithToken<
      GetResourcesWithPermissionInput,
      GetResourcesWithPermissionOutput
    >({
      method: 'POST',
      path: '/permissions/get-resources-with-permission',
      body: input,
      metadata: metadata,
    })

    return getResourcesWithPermissionsOutput
  }

  /* ---------------------------- User Grant ---------------------------- */

  getUserGrant = async (
    input: GetUserGrantInput,
    metadata?: DroidMapMetadata,
  ): Promise<UserGrant> => {
    const userGrant = await this.callApiRouteWithToken<
      GetUserGrantInput,
      UserGrant
    >({
      method: 'GET',
      path: `/user-grants/${input.userId}/${input.resourceId}/${input.role}/${input.grantType}`,
      body: input,
      metadata,
    })

    return userGrant
  }

  listUserGrantsByUser = async (
    input: ListUserGrantsByUserInput,
    metadata?: DroidMapMetadata,
  ): Promise<Paged<UserGrant>> => {
    const { userId, next, prev, pageSize } = input

    const queryParameters: QueryParameterBag =
      queryParameterBagFromPaginationControls({
        next,
        prev,
        pageSize,
      })

    const userGrants = await this.callApiRouteWithToken<
      ListUserGrantsByUserInput,
      Paged<UserGrant>
    >({
      method: 'GET',
      path: `/user-grants/user/${userId}`,
      queryParameters,
      metadata,
    })

    return userGrants
  }

  listUserGrantsByUserAndResource = async (
    input: ListUserGrantsByUserAndResourceInput,
    metadata?: DroidMapMetadata,
  ): Promise<Paged<UserGrant>> => {
    const { userId, resourceId, next, prev, pageSize } = input

    const queryParameters: QueryParameterBag =
      queryParameterBagFromPaginationControls({
        next,
        prev,
        pageSize,
      })

    const userGrants = await this.callApiRouteWithToken<
      ListUserGrantsByUserAndResourceInput,
      Paged<UserGrant>
    >({
      method: 'GET',
      path: `/user-grants/user/${userId}/resource/${resourceId}`,
      queryParameters,
      metadata,
    })

    return userGrants
  }

  listUserGrantsByGroup = async (
    input: ListUserGrantsByGroupAndResourceAndRoleInput,
    metadata?: DroidMapMetadata,
  ): Promise<Paged<UserGrant>> => {
    const { groupId, resourceId, role, next, prev, pageSize } = input

    const queryParameters: QueryParameterBag =
      queryParameterBagFromPaginationControls({
        next,
        prev,
        pageSize,
      })

    const userGrants = await this.callApiRouteWithToken<
      ListUserGrantsByGroupAndResourceAndRoleInput,
      Paged<UserGrant>
    >({
      method: 'GET',
      path: `/user-grants/group/${groupId}/resource/${resourceId}/role/${role}`,
      queryParameters,
      metadata,
    })

    return userGrants
  }
}
